import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories, genres, rankings } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function getComicById(id: string): Comic | undefined {
  return comics.find((c) => c.id === id);
}

function RankingList({ ids, type }: { ids: string[]; type: 'hot' | 'rating' }) {
  const label = type === 'hot' ? '人气' : '评分';
  const badgeClass = type === 'hot' ? 'bg-primary-500' : 'bg-accent-500';

  return (
    <div className="flex flex-col gap-2">
      {ids.slice(0, 5).map((id, index) => {
        const comic = getComicById(id);
        if (!comic) return null;
        return (
          <Link
            key={id}
            to={`/comic/${comic.id}`}
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors group"
          >
            <span className={`w-6 h-6 flex items-center justify-center rounded text-xs font-bold text-background-50 flex-shrink-0 ${
              index < 3 ? badgeClass : 'bg-secondary-400'
            }`}>
              {index + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors truncate">
                {comic.title}
              </p>
              <p className="text-xs text-foreground-500 mt-0.5">{comic.category} · {comic.author}</p>
            </div>
            <span className="text-xs text-primary-600 font-medium flex items-center gap-0.5 whitespace-nowrap">
              <i className="ri-star-fill text-[10px]"></i>
              {comic.rating}
            </span>
          </Link>
        );
      })}
      <Link
        to={`/category/${type === 'hot' ? '韩漫' : '日漫'}`}
        className="text-xs text-primary-600 hover:text-primary-700 font-medium text-center mt-1"
      >
        查看全部 <i className="ri-arrow-right-line ml-1"></i>
      </Link>
    </div>
  );
}

export default function Home() {
  const [activeRankTab, setActiveRankTab] = useState<'hot' | 'rating'>('hot');

  const featuredComics = comics.filter((c) => c.featured);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '香蕉漫画',
    url: 'https://bananamanga.com',
    description: '香蕉漫画精选热门韩漫、日漫、国漫分类推荐，涵盖恋爱、奇幻、热血等所有风格，为漫迷提供高清版漫画免费在线阅读和人气排行榜。',
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '香蕉漫画',
    url: 'https://bananamanga.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://bananamanga.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '香蕉漫画有哪些漫画分类？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '香蕉漫画将漫画分为韩漫、日漫、国漫三大类，每个大类下按风格分为恋爱、奇幻、热血、悬疑、搞笑、都市、古风、校园、冒险等多个子分类，方便漫迷快速找到喜欢的漫画类型。',
        },
      },
      {
        '@type': 'Question',
        name: '香蕉漫画的排行榜是如何计算的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '排行榜综合了人气热度（浏览量）、读者评分、更新频率和社会讨论度等多个维度，提供热门榜、新作榜和评分榜三个榜单。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[520px] md:h-[620px] flex items-center">
          <img
            src="https://readdy.ai/api/search-image?query=Vibrant%20artistic%20anime%20manga%20style%20hero%20banner%20background%2C%20colorful%20abstract%20illustration%20with%20warm%20orange%20red%20coral%20gradient%20ink%20wash%20texture%2C%20Japanese%20and%20Korean%20comic%20art%20elements%20floating%2C%20dynamic%20brush%20strokes%2C%20modern%20minimalist%20composition%2C%20no%20text%2C%20warm%20toned%20artistic%20background%20with%20rich%20texture&width=1600&height=700&seq=hero-bg-manga-01&orientation=landscape"
            alt="香蕉漫画 - 韩漫日漫国漫推荐大全"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-accent-400 font-medium text-sm md:text-base mb-3 tracking-wider">
                韩漫 · 日漫 · 国漫
              </p>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                香蕉漫画
                <span className="block text-xl md:text-2xl font-normal mt-2 text-white/90">
                  热门漫画推荐大全
                </span>
              </h1>
              <p className="text-white/75 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-8">
                精选热门韩漫、日漫、国漫分类推荐，涵盖恋爱、奇幻、热血等所有风格，
                为漫迷提供高清版漫画免费在线阅读和人气排行榜
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.key}
                    to={`/category/${cat.key}`}
                    className="px-6 py-3 bg-white/15 backdrop-blur-sm hover:bg-white/25 text-white rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 border border-white/20"
                  >
                    {cat.label}推荐
                  </Link>
                ))}
                <Link
                  to="/about"
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Category Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                漫画分类
              </h2>
              <p className="text-foreground-500 text-sm mt-2">按地区分类，快速发现你喜欢的漫画</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <Link
                to="/category/韩漫"
                className="group relative overflow-hidden rounded-xl h-60"
              >
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20romance%20fantasy%20aesthetic%20background%2C%20soft%20warm%20pink%20and%20coral%20tone%2C%20beautiful%20illustration%20texture%2C%20dreamy%20atmosphere&width=800&height=500&seq=cat-bg-kr&orientation=landscape"
                  alt="韩漫"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-heading text-xl font-bold text-white mb-1">韩漫</h3>
                  <p className="text-white/75 text-sm">韩国漫画以其精致的画风和独特的叙事风格深受读者喜爱</p>
                </div>
              </Link>
              <Link
                to="/category/日漫"
                className="group relative overflow-hidden rounded-xl h-60"
              >
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20manga%20style%20action%20epic%20aesthetic%20background%2C%20dynamic%20warm%20orange%20and%20red%20tone%2C%20exciting%20illustration%20texture%2C%20dramatic%20atmosphere&width=800&height=500&seq=cat-bg-jp&orientation=landscape"
                  alt="日漫"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-heading text-xl font-bold text-white mb-1">日漫</h3>
                  <p className="text-white/75 text-sm">日本漫画拥有最丰富的题材和最成熟的产业体系</p>
                </div>
              </Link>
              <Link
                to="/category/国漫"
                className="group relative overflow-hidden rounded-xl h-60"
              >
                <img
                  src="https://readdy.ai/api/search-image?query=Chinese%20manhua%20style%20ancient%20fantasy%20aesthetic%20background%2C%20elegant%20ink%20wash%20and%20gold%20tone%2C%20traditional%20illustration%20texture%2C%20refined%20atmosphere&width=800&height=500&seq=cat-bg-cn&orientation=landscape"
                  alt="国漫"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-heading text-xl font-bold text-white mb-1">国漫</h3>
                  <p className="text-white/75 text-sm">国产漫画近年迅速崛起，涌现大量优质原创作品</p>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Featured Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  <i className="ri-fire-line text-accent-500 mr-2"></i>
                  精选推荐
                </h2>
                <p className="text-foreground-500 text-sm mt-2">编辑精心挑选，不容错过的热门好漫</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {featuredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Rankings + Genres */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Rankings */}
              <div className="lg:col-span-1">
                <div className="bg-background-100 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="font-heading text-lg font-bold text-foreground-950">
                      <i className="ri-medal-line text-accent-500 mr-1.5"></i>
                      排行榜
                    </h2>
                    <div className="flex bg-background-200/70 rounded-full p-0.5">
                      <button
                        onClick={() => setActiveRankTab('hot')}
                        className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                          activeRankTab === 'hot' ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
                        }`}
                      >
                        人气榜
                      </button>
                      <button
                        onClick={() => setActiveRankTab('rating')}
                        className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                          activeRankTab === 'rating' ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
                        }`}
                      >
                        评分榜
                      </button>
                    </div>
                  </div>
                  <RankingList ids={activeRankTab === 'hot' ? rankings.hot : rankings.rating} type={activeRankTab} />
                </div>
              </div>

              {/* Genres Cloud */}
              <div className="lg:col-span-2">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5">
                  <i className="ri-price-tag-3-line text-accent-500 mr-1.5"></i>
                  风格标签
                </h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {genres.map((genre) => (
                    <Link
                      key={genre.key}
                      to={`/category/韩漫?genre=${genre.key}`}
                      className="px-3 py-2.5 bg-background-100 hover:bg-primary-50 text-foreground-700 hover:text-primary-700 rounded-lg text-sm text-center font-medium whitespace-nowrap transition-colors cursor-pointer"
                    >
                      {genre.label}
                    </Link>
                  ))}
                </div>

                {/* Latest Updates */}
                <h2 className="font-heading text-lg font-bold text-foreground-950 mt-8 mb-5">
                  <i className="ri-time-line text-accent-500 mr-1.5"></i>
                  最近更新
                </h2>
                <div className="space-y-2">
                  {comics.slice(0, 6).map((comic) => (
                    <Link
                      key={comic.id}
                      to={`/comic/${comic.id}`}
                      className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-background-100 transition-colors group"
                    >
                      <img
                        src={comic.coverImage}
                        alt={comic.title}
                        className="w-12 h-16 object-cover rounded flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors truncate">
                          {comic.title}
                        </p>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          {comic.category} · 更新至{comic.totalChapters}话
                        </p>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{comic.updatedAt}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-24">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20artistic%20manga%20illustration%20background%20with%20soft%20orange%20red%20coral%20gradient%2C%20Japanese%20Korean%20comic%20art%20abstract%20texture%2C%20floating%20ink%20brush%20elements%2C%20modern%20minimalist%20composition%2C%20rich%20warm%20color%20palette%2C%20no%20text%2C%20beautiful%20atmospheric%20background&width=1600&height=400&seq=cta-bg-manga-01&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative z-10 w-full px-4 md:px-6 max-w-[1280px] mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              发现你的下一部心头好
            </h2>
            <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto mb-8">
              无论你是韩漫粉、日漫控还是国漫爱好者，香蕉漫画都为你的漫荒时刻准备好了最佳推荐
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/category/${cat.key}`}
                  className="px-6 py-2.5 bg-white/15 hover:bg-white/25 text-white rounded-full text-sm font-medium whitespace-nowrap transition-all border border-white/20"
                >
                  浏览{cat.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}